import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import i18n from '../../services/i18n';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';
import { userSelector, login, clearState } from './userSlice';
import { themeSelector, selectTheme } from '../theme/themeSlice';

import { Formik } from 'formik';
import * as yup from 'yup';

import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import PasswordIcon from '@mui/icons-material/Password';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const styleGrow = {
  flexGrow: 1,
};

function Login() {
  const { t } = useTranslation('user');

  const [errors, setErrors] = useState([]);

  const [showPassword, setShowPassword] = useState(false);

  const isHeightSufficient = useMediaQuery(`(min-height: 900px)`);
  // const isLandscape = useMediaQuery('(orientation: landscape)');

  const { user, isFetching, isSuccess, isError, errorMessage } =
    useSelector(userSelector);
  const { theme } = useSelector(themeSelector);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const onSubmit = (values) => {
    setErrors([]);
    dispatch(login(values));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownShowPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, []);

  useEffect(() => {
    if (isError) {
      setErrors(errorMessage);
      dispatch(clearState());
    }

    if (isSuccess) {
      if (user.language && user.language !== i18n.language) {
        i18n.changeLanguage(user.language);
      }
      if (user.theme && user.theme !== theme) {
        dispatch(selectTheme(user.theme));
      }
      dispatch(clearState());
      navigate('/');
    }
  }, [isError, isSuccess]);

  if (user) {
    return <Navigate to="/" />;
  }

  return (
    <Container sx={{ mt: 2 }} maxWidth="xs">
      <Paper
        sx={{ pt: 2, pb: 2, pl: 2, pr: 2, textAlign: 'center' }}
        variant="outlined"
      >
        {isHeightSufficient ? (
          <Box>
            <AccountCircleIcon sx={{ height: '5em', width: '5em' }} />
          </Box>
        ) : null}
        <Formik
          validateOnMount
          initialValues={{
            username: '',
            password: '',
          }}
          validationSchema={yup.object().shape({
            username: yup.string().required('common:required_field'),
            password: yup.string().required('common:required_field'),
          })}
          onSubmit={(values) => {
            onSubmit(values);
          }}
        >
          {({
            isValid,
            dirty,
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
          }) => (
            <form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Box>
                <Grid container spacing={1}>
                  <Grid item>
                    <Box mt={3}>
                      <PersonIcon />
                    </Box>
                  </Grid>
                  <Grid item sx={{ ...styleGrow }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      color="primary"
                      required
                      inputProps={{
                        autoCapitalize: 'none',
                      }}
                      id="username"
                      name="username"
                      label={t('username')}
                      value={values.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username && t(errors.username)}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mt={3}>
                <Grid container spacing={1}>
                  <Grid item>
                    <Box mt={3}>
                      <PasswordIcon />
                    </Box>
                  </Grid>
                  <Grid item sx={{ ...styleGrow }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      required
                      // type="password"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              // aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownShowPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      id="password"
                      name="password"
                      label={t('password')}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && t(errors.password)}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box mt={8}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{
                    py: 2,
                  }}
                  fullWidth
                  // disableElevation
                  disabled={!(isValid && dirty) || isFetching}
                  // loading={isFetching}
                  // loadingPosition="start"
                  startIcon={
                    isFetching ? (
                      <CircularProgress color="inherit" size={'20px'} />
                    ) : (
                      <LoginIcon />
                    )
                  }
                >
                  {t('login')}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
        {errors.length > 0 && (
          <Box mt={5}>
            {errors
              .slice()
              .reverse()
              .map((error) => (
                <Box key={error} mt={1}>
                  <Alert variant="outlined" severity="error">
                    {t(error)}
                  </Alert>
                </Box>
              ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default Login;
